import { useMemo } from 'react';
import { adoptionReport, topNWithOther } from '../../../../lib/adoptionAnalytics.js';
import { AdoptionChart } from '../charts.jsx';

const fmtDate = (d) => (d ? d : '—');

function ActivityBars({ rows, total, perUser }) {
  const max = Math.max(1, ...rows.map((r) => r.interactions));
  return (
    <div className="ar-orglist">
      {rows.map((r) => {
        const pct = total ? Math.round((100 * r.interactions) / total) : 0;
        const label = perUser ? r.name.split('@')[0] : r.name;
        return (
          <div className="ar-org" key={r.name}>
            <div className="ar-org-name" title={r.name}>{label}{r.other ? ` (${r.other})` : ''}</div>
            <div className="ar-org-bar"><i style={{ width: `${(100 * r.interactions) / max}%` }} /></div>
            <div className="ar-org-val">{r.interactions} <span className="ar-dim">({pct}%)</span></div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Executive Platform Adoption view. scope === 'global' renders the org-level
 * deck; otherwise `scope` is a company name and `events` should be that
 * company's events (right panel switches to per-user).
 */
export default function AdoptionReport({ events, aliases, scope = 'global' }) {
  const isGlobal = scope === 'global';
  const r = useMemo(() => adoptionReport(events, { aliases }), [events, aliases]);

  if (!r.weeks.length) return <div className="status">No activity in this snapshot{isGlobal ? '' : ` for ${scope}`}.</div>;

  const growth = r.growthPct == null ? '—' : `${r.growthPct >= 0 ? '+' : ''}${r.growthPct}%`;
  const activityRows = isGlobal
    ? topNWithOther(r.byOrg, 5, 'Other biotech / pharma')
    : topNWithOther(r.byUser, 6, 'Other users');

  return (
    <div className="ar">
      <div className="ar-head">
        <h1 className="ar-title">{isGlobal ? 'Platform Adoption' : scope}</h1>
        <div className="ar-sub">
          {r.daysActive.total}-day overview — {fmtDate(r.dateRange[0])} to {fmtDate(r.dateRange[1])} · {isGlobal ? 'External customers' : 'Single organization'}
        </div>
      </div>

      <div className="ar-kpis">
        <div className="ar-kpi">
          <div className="ar-kpi-v">{r.interactions}</div>
          <div className="ar-kpi-l">Research Interactions</div>
          <div className="ar-kpi-s">{growth} 2nd half vs 1st</div>
        </div>
        <div className="ar-kpi">
          <div className="ar-kpi-v">{r.scientists}</div>
          <div className="ar-kpi-l">Scientists Adopted</div>
          <div className="ar-kpi-s">{isGlobal ? `Across ${r.organizations.total} organizations` : `${scope}`}</div>
        </div>
        {isGlobal ? (
          <div className="ar-kpi">
            <div className="ar-kpi-v">{r.organizations.total}</div>
            <div className="ar-kpi-l">Organizations Engaged</div>
            <div className="ar-kpi-s">{r.organizations.named} named, {r.organizations.emerging} emerging</div>
          </div>
        ) : (
          <div className="ar-kpi">
            <div className="ar-kpi-v">{r.championsCount}</div>
            <div className="ar-kpi-l">Champions</div>
            <div className="ar-kpi-s">10+ interactions</div>
          </div>
        )}
        <div className="ar-kpi">
          <div className="ar-kpi-v">{r.daysActive.pct}%</div>
          <div className="ar-kpi-l">Days Active ({r.daysActive.active}/{r.daysActive.total})</div>
          <div className="ar-kpi-s">{r.avgQueriesPerDay} avg interactions/day</div>
        </div>
      </div>

      <div className="ar-grid">
        <div className="ar-panel">
          <h2>{isGlobal ? 'Cumulative Scientist Adoption' : 'Cumulative User Adoption'}</h2>
          <AdoptionChart report={r} label={isGlobal ? 'scientists' : 'users'} />
          <div className="ar-legend">
            <span className="ar-leg"><span className="ar-sw" style={{ background: '#bcd6d4' }} />Weekly interactions</span>
            <span className="ar-leg"><span className="ar-sw" style={{ background: 'var(--teal)' }} />Cumulative {isGlobal ? 'scientists' : 'users'}</span>
            <span className="ar-leg"><span className="ar-sw dash" />Champions (10+ interactions)</span>
          </div>
        </div>
        <div className="ar-panel">
          <h2>{isGlobal ? 'Activity by Organization' : 'Activity by User'}</h2>
          <div className="ar-sub2">{r.interactions} total research interactions</div>
          <ActivityBars rows={activityRows} total={r.interactions} perUser={!isGlobal} />
        </div>
      </div>

      <div className="ar-foot">Plex Research · Platform Usage Report · Confidential</div>
    </div>
  );
}
